import {
  SupplierDetailForm,
  SupplierDetailApiData,
  SupplierRegistrationForm,
  SupplierType,
  SupplierCategory,
  RawMaterial,
  CooperativeUnion,
  FactoryContact,
} from '../../types/company.types';
import { DepartmentResponse, Staff } from '../../types';
import { CreateSupplierRequest } from '../../services/companies.service';

import {
  mapRepresentativesToDepartmentContactLists,
  mapDepartmentContactListsToRepresentatives,
} from './common-mapper';

import { CONTACT_ROLE_TYPES } from '@/lib/constants';
import { padPostalCode } from '@/utils/helpers';
const isContactRoleType = (value: unknown): value is (typeof CONTACT_ROLE_TYPES)[number] =>
  typeof value === 'string' && CONTACT_ROLE_TYPES.some((role) => role === value);

const mapRoleTypeToDivision = (roleType: unknown): string | undefined => {
  if (!isContactRoleType(roleType)) {
    return undefined;
  }

  return (CONTACT_ROLE_TYPES.indexOf(roleType) + 1).toString();
};

const mapDivisionToRoleType = (division: unknown): (typeof CONTACT_ROLE_TYPES)[number] => {
  const parsedDivision = Number(division);

  if (
    Number.isInteger(parsedDivision) &&
    parsedDivision >= 1 &&
    parsedDivision <= CONTACT_ROLE_TYPES.length
  ) {
    return CONTACT_ROLE_TYPES[parsedDivision - 1];
  }

  if (isContactRoleType(division)) {
    return division;
  }

  return CONTACT_ROLE_TYPES[0];
};

/**
 * Maps API response to supplier detail form data
 */
export const loadSupplierDataFromApi = (
  apiData: SupplierDetailApiData,
  departments: DepartmentResponse[] = [],
  _staffsByDepartment: Record<string, Staff[]> = {}
): SupplierDetailForm => {
  const {
    departmentContactLists = [],
    contactOfficerLists = [],
    partnerAssociationLists = [],
    materialLists = [],
    aggregateCoarseLists = [],
    aggregateFineLists = [],
    locationAddress1,
    locationAddress2,
    locationAddress3,
    headquartersAddress1,
    headquartersAddress2,
    headquartersAddress3,
    factoryLocation,
    factoryJisCertNo,
    factoryCertClassification,
    factoryHighStrengthCertNo,
    industryAssociation,
    qualifiedEngineerConstructionDepartment,
    qualifiedConcreteDiagnostician,
    qualifiedConcreteChiefEngineer,
    qualifiedConcreteEngineer,
    mixerNominalCapacity,
    mixerFormat,
    mixerCapacity,
    mixerCardinality,
    mixerProductionCompany,
    mixerWeighingMethod,
    carrierLarge10t,
    carrierMediumSize8t,
    carrierMediumSize7t,
    carrierMediumSize5t,
    carrierSmall4t,
    carrierM3Type3t,
    carrierM3Type2t,
    other,
    createdAt,
    createdUser,
    updatedAt,
    updatedUser,
  } = apiData;

  // Helper functions to convert between API numbers and form strings
  const convertSupplierTypeToString = (type?: number): SupplierType => {
    switch (type) {
      case 1:
        return '組合'; // ASSOCIATION
      case 2:
        return '販売店'; // DEALER
      case 3:
        return 'プラント'; // PLANT
      default:
        return '組合'; // Default to ASSOCIATION
    }
  };

  const convertSupplierCategoryToString = (category?: number): SupplierCategory => {
    switch (category) {
      case 1:
        return 'ゼネコン'; // GENERAL_CONTRACTOR
      case 2:
        return 'ハウスメーカー'; // HOUSE_MANUFACTURER
      case 3:
        return '商社販売店'; // TRADING_COMPANY
      case 4:
        return '生コンプラント'; // CONCRETE_PLANT
      case 5:
        return 'その他'; // OTHER
      default:
        return 'ゼネコン'; // Default to GENERAL_CONTRACTOR
    }
  };

  const mappedData: SupplierDetailForm = {
    // Basic Company Info
    galiSupplierCode:
      apiData.galiAccountCode !== null && apiData.galiAccountCode !== undefined
        ? apiData.galiAccountCode.toString()
        : '',
    officialName: apiData.formalName || '',
    abbreviatedName: apiData.abbreviationName || '',
    branchDepartment: apiData.branchDepartment || '',
    supplierNameKatakana: apiData.partnerNameKana || '',
    supplierType: convertSupplierTypeToString(apiData.supplierType),
    supplierCategory: convertSupplierCategoryToString(apiData.customerCategory),

    // Address Information
    currentAddress: {
      postalCode1: padPostalCode(apiData.locationZipCodeFirst, 3),
      postalCode2: padPostalCode(apiData.locationZipCodeLast, 4),
      address1: locationAddress1 || '',
      address2: locationAddress2 || '',
      address3: locationAddress3 || '',
      phone: apiData.locationPhone || '',
      fax: apiData.locationFax || '',
    },
    headOfficeAddress: {
      postalCode1: padPostalCode(apiData.headquartersAddressZipCodeFirst, 3),
      postalCode2: padPostalCode(apiData.headquartersAddressZipCodeLast, 4),
      address1: headquartersAddress1 || '',
      address2: headquartersAddress2 || '',
      address3: headquartersAddress3 || '',
    },

    // Nohara Representatives (same as construction-company-detail)
    representatives: mapDepartmentContactListsToRepresentatives(
      departmentContactLists || [],
      departments
    ),

    // Factory Information
    factoryName: apiData.factoryName || '',
    factoryAddress: factoryLocation || '',
    factoryPostalCode1: padPostalCode(apiData.factoryLocationZipCodeFirst, 3),
    factoryPostalCode2: padPostalCode(apiData.factoryLocationZipCodeLast, 4),
    factoryContacts: (() => {
      const contacts = new Array(contactOfficerLists.length);
      for (let i = 0; i < contactOfficerLists.length; i++) {
        const officer = contactOfficerLists[i];
        const division = mapDivisionToRoleType(officer.division);
        contacts[i] = {
          id: officer.id || `factory-contact-${i}`,
          roleType: division,
          department: officer.department || '',
          contactPerson: officer.personName || '',
          phone: officer.phoneNumber || '',
          email: officer.emailAddress || '',
          fax: officer.fax || '',
          index: i,
        };
      }
      return contacts;
    })(),

    // Certification Information
    certificationInfo: {
      jisNumber: factoryJisCertNo || '',
      certificationType: factoryCertClassification || undefined,
      highStrengthNumber: factoryHighStrengthCertNo || '',
    },

    // Union Membership
    unionMembership: {
      industrialUnion: industryAssociation || '',
      cooperativeUnions: (() => {
        const unions = new Array(partnerAssociationLists.length);
        for (let i = 0; i < partnerAssociationLists.length; i++) {
          const assoc = partnerAssociationLists[i];
          unions[i] = {
            id: assoc.id || `union-${i}`,
            name: assoc.name || '',
            index: i,
          };
        }
        return unions;
      })(),
    },

    // Qualified Personnel
    qualifiedPersonnel: {
      professionalEngineers: qualifiedEngineerConstructionDepartment ?? '',
      concreteDiagnosticians: qualifiedConcreteDiagnostician ?? '',
      seniorConcreteTechnicians: qualifiedConcreteChiefEngineer ?? '',
      concreteTechnicians: qualifiedConcreteEngineer ?? '',
    },

    // Raw Materials - Optimized with pre-allocation
    rawMaterials: (() => {
      // Pre-allocate arrays for better performance
      const cement = new Array(materialLists.length);
      const coarseAggregate = new Array(aggregateCoarseLists.length);
      const fineAggregate = new Array(aggregateFineLists.length);

      // Use for loops for better performance than map
      for (let i = 0; i < materialLists.length; i++) {
        const material = materialLists[i];

        // Handle cement type mapping - check if it's a custom type
        let cementType;
        let otherType = '';

        // If cement_type_code is null/0 or if cement_type_other exists, it's a custom type
        if (!material.cementTypeCode || material.cementTypeOther) {
          cementType = 'other';
          otherType = material.cementTypeOther || material.cementTypeName || '';
        } else {
          cementType = material.cementTypeCode.toString();
        }

        cement[i] = {
          id: material.id || `cement-${i}`,
          type: cementType,
          manufacturerOrOrigin: material.maker || '',
          otherType: otherType,
          index: i,
        };
      }

      for (let i = 0; i < aggregateCoarseLists.length; i++) {
        const agg = aggregateCoarseLists[i];
        coarseAggregate[i] = {
          id: agg.id || `coarse-${i}`,
          type: agg.type || '',
          manufacturerOrOrigin: agg.producingArea || '',
          hasLimestone: agg.limestoneExists || false,
          index: i,
        };
      }

      for (let i = 0; i < aggregateFineLists.length; i++) {
        const agg = aggregateFineLists[i];
        fineAggregate[i] = {
          id: agg.id || `fine-${i}`,
          type: agg.type || '',
          manufacturerOrOrigin: agg.producingArea || '',
          hasLimestone: agg.limestoneExists || false,
          index: i,
        };
      }

      return { cement, coarseAggregate, fineAggregate };
    })(),

    // Mixer Information
    mixerInfo: {
      ratedCapacity: mixerNominalCapacity || '',
      type: mixerFormat || '',
      mixerCapacity: mixerCapacity || '',
      mixerCardinality: mixerCardinality?.toString() || '',
      manufacturer: mixerProductionCompany || '',
      measurementMethod: mixerWeighingMethod || '',
    },

    // Transport Vehicles
    transportVehicles: {
      large10t: carrierLarge10t ?? '',
      medium8t: carrierMediumSize8t ?? '',
      size7t: carrierMediumSize7t ?? '',
      size5t: carrierMediumSize5t ?? '',
      small4t: carrierSmall4t ?? '',
      size3t: carrierM3Type3t ?? '',
      size2t: carrierM3Type2t ?? '',
      other: other ?? '',
    },

    // Common cement flag
    isCommonCement: apiData.isCommonCement ?? false,

    // Audit Information
    createdAt,
    createdUser,
    updatedAt,
    updatedUser,
    createdStaff: apiData.createdStaff,
    updatedStaff: apiData.updatedStaff,
    canUpdate: apiData.canUpdate ?? false,
  };

  return mappedData;
};

/**
 * Maps supplier detail form data to API request format for editing
 * Converts form field names to API field names and IDs to codes
 */
export const mappingEditSupplierData = (
  formData: SupplierDetailForm,
  departments: DepartmentResponse[] = []
): CreateSupplierRequest => {
  const apiRequest: CreateSupplierRequest = {
    partnerClassification: 2, // Supplier
    galiAccountCode: toNumber(formData.galiSupplierCode),
    formalName: formData.officialName,
    abbreviationName: formData.abbreviatedName,
    branchDepartment: formData.branchDepartment || undefined,
    partnerNameKana: formData.supplierNameKatakana,
    supplierType: getSupplierTypeNumber(formData.supplierType),
    customerCategory: getSupplierCategoryNumber(formData.supplierCategory),

    // Location address
    locationZipCodeFirst: toNumber(formData.currentAddress.postalCode1, 0) || 0,
    locationZipCodeLast: toNumber(formData.currentAddress.postalCode2, 0) || 0,
    locationAddress1: formData.currentAddress.address1,
    locationAddress2: formData.currentAddress.address2,
    locationAddress3: formData.currentAddress.address3 || undefined,
    locationPhone: formData.currentAddress.phone,
    locationFax: formData.currentAddress.fax,

    // Headquarters address
    headquartersAddressZipCodeFirst: toNumber(formData.headOfficeAddress.postalCode1, 0) || 0,
    headquartersAddressZipCodeLast: toNumber(formData.headOfficeAddress.postalCode2, 0) || 0,
    headquartersAddress1: formData.headOfficeAddress.address1,
    headquartersAddress2: formData.headOfficeAddress.address2,
    headquartersAddress3: formData.headOfficeAddress.address3 || undefined,

    // Factory information
    factoryName: formData.factoryName || undefined,
    factoryLocationZipCodeFirst: toNumber(formData.factoryPostalCode1),
    factoryLocationZipCodeLast: toNumber(formData.factoryPostalCode2),
    factoryLocation: formData.factoryAddress || undefined,

    // Certifications
    factoryJisCertNo: formData.certificationInfo?.jisNumber || undefined,
    factoryCertClassification: formData.certificationInfo?.certificationType || undefined,
    factoryHighStrengthCertNo: formData.certificationInfo?.highStrengthNumber || undefined,

    // Union membership
    industryAssociation: formData.unionMembership.industrialUnion || undefined,

    // Qualified personnel - convert to numbers
    qualifiedEngineerConstructionDepartment: toNumber(
      formData.qualifiedPersonnel.professionalEngineers
    ),
    qualifiedConcreteDiagnostician: toNumber(formData.qualifiedPersonnel.concreteDiagnosticians),
    qualifiedConcreteChiefEngineer: toNumber(formData.qualifiedPersonnel.seniorConcreteTechnicians),
    qualifiedConcreteEngineer: toNumber(formData.qualifiedPersonnel.concreteTechnicians),

    // Mixer information
    mixerNominalCapacity: formData.mixerInfo.ratedCapacity || undefined,
    mixerFormat: formData.mixerInfo.type || undefined,
    mixerCapacity: formData.mixerInfo.mixerCapacity || undefined,
    mixerCardinality: toNumber(formData.mixerInfo.mixerCardinality),
    mixerProductionCompany: formData.mixerInfo.manufacturer || undefined,
    mixerWeighingMethod: formData.mixerInfo.measurementMethod || undefined,

    // Transport vehicles - convert to numbers
    carrierLarge10t: toNumber(formData.transportVehicles.large10t),
    carrierMediumSize8t: toNumber(formData.transportVehicles.medium8t),
    carrierMediumSize7t: toNumber(formData.transportVehicles.size7t),
    carrierMediumSize5t: toNumber(formData.transportVehicles.size5t),
    carrierSmall4t: toNumber(formData.transportVehicles.small4t),
    carrierM3Type3t: toNumber(formData.transportVehicles.size3t),
    carrierM3Type2t: toNumber(formData.transportVehicles.size2t),
    other: formData.transportVehicles?.other || undefined,

    // Department contacts (minimum 1 required)
    departmentContactLists: mapRepresentativesToDepartmentContactLists(
      formData.representatives,
      departments
    ),

    // Contact officers - filter valid contacts, send empty array if none
    contactOfficerLists: createContactOfficerLists(formData.factoryContacts),

    // Partner associations - filter valid unions, send empty array if none
    partnerAssociations: createPartnerAssociations(formData.unionMembership.cooperativeUnions),

    // Materials - filter valid materials, send empty array if none
    materials: createMaterials(formData.rawMaterials.cement),

    // Aggregates - filter valid aggregates, send empty array if none
    aggregateCoarseLists: createAggregateLists(formData.rawMaterials.coarseAggregate),

    aggregateFineLists: createAggregateLists(formData.rawMaterials.fineAggregate),

    // Common cement flag
    isCommonCement: formData.isCommonCement ?? false,
  };

  // Clean the request data to remove empty fields that might cause validation errors
  const cleanedRequest = cleanApiRequest(apiRequest) as CreateSupplierRequest;

  // Always ensure these fields are present in the payload, even if empty
  // This is crucial for clearing existing data when all items are empty
  if (!cleanedRequest.contactOfficerLists) {
    cleanedRequest.contactOfficerLists = [];
  }
  if (!cleanedRequest.partnerAssociations) {
    cleanedRequest.partnerAssociations = [];
  }
  if (!cleanedRequest.materials) {
    cleanedRequest.materials = [];
  }
  if (!cleanedRequest.aggregateCoarseLists) {
    cleanedRequest.aggregateCoarseLists = [];
  }
  if (!cleanedRequest.aggregateFineLists) {
    cleanedRequest.aggregateFineLists = [];
  }

  // Additional check to ensure no empty email fields
  if (cleanedRequest.contactOfficerLists) {
    cleanedRequest.contactOfficerLists.forEach(
      (officer: {
        displayOrder?: number;
        division?: string;
        department?: string;
        personName?: string;
        phoneNumber?: string;
        emailAddress?: string;
        fax?: string;
      }) => {
        if (
          officer.emailAddress === '' ||
          officer.emailAddress === null ||
          officer.emailAddress === undefined
        ) {
          delete officer.emailAddress; // Remove the empty email field
        }
      }
    );
  }

  return cleanedRequest;
};

/**
 * Helper function to validate email format
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false;
  const trimmed = email.trim();
  return (
    trimmed.length > 0 &&
    trimmed.includes('@') &&
    trimmed.includes('.') &&
    trimmed.indexOf('@') > 0 &&
    trimmed.indexOf('@') < trimmed.lastIndexOf('.')
  );
};

/**
 * Helper function to convert value to number or undefined
 */
export const toNumber = (value: unknown, defaultValue?: number): number | undefined => {
  if (value == null || value === '') return defaultValue ?? undefined;
  const num = Number(value);
  return Number.isNaN(num) ? (defaultValue ?? undefined) : num;
};

/**
 * Helper function to convert supplier type enum to number
 * @param type - The supplier type string
 */
export const getSupplierTypeNumber = (type?: string): number => {
  switch (type) {
    case '組合':
      return 1; // ASSOCIATION
    case '販売店':
      return 2; // DEALER
    case 'プラント':
      return 3; // PLANT
    default:
      return 1; // Default to ASSOCIATION
  }
};

/**
 * Helper function to convert supplier category enum to number
 * @param category - The supplier category string
 */
export const getSupplierCategoryNumber = (category?: string): number => {
  switch (category) {
    case 'ゼネコン':
      return 1; // GENERAL_CONTRACTOR
    case 'ハウスメーカー':
      return 2; // HOUSE_MANUFACTURER
    case '商社販売店':
      return 3; // TRADING_COMPANY
    case '生コンプラント':
      return 4; // CONCRETE_PLANT
    case 'その他':
      return 5; // OTHER
    default:
      return 1; // Default to GENERAL_CONTRACTOR
  }
};

/**
 * Helper function to create aggregate lists
 */
export const createAggregateLists = (
  aggregates: RawMaterial[]
): Array<{
  displayOrder: number;
  type: string;
  producingArea: string;
  limestoneExists: boolean;
}> => {
  const validAggregates = aggregates
    .filter((agg) => agg.type?.trim() && agg.manufacturerOrOrigin?.trim())
    // Remove duplicates based on type and producing_area
    .filter(
      (agg, index, arr) =>
        arr.findIndex(
          (a) =>
            a.type?.trim() === agg.type?.trim() &&
            a.manufacturerOrOrigin?.trim() === agg.manufacturerOrOrigin?.trim()
        ) === index
    );

  // If no valid aggregates, send empty array
  if (validAggregates.length === 0) {
    return [];
  }

  return validAggregates
    .map((agg, index) => {
      const displayOrder = index + 1;
      const aggregateItem = {
        displayOrder: Number(displayOrder),
        type: String(agg.type?.trim() || ''),
        producingArea: String(agg.manufacturerOrOrigin?.trim() || ''),
        limestoneExists: Boolean(agg.hasLimestone || false),
      };

      // Validate that required fields are not empty
      if (!aggregateItem.type || !aggregateItem.producingArea) {
        return null; // Return null for invalid items
      }

      return aggregateItem;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);
};

/**
 * Helper function to create contact officer lists
 */
export const createContactOfficerLists = (
  contacts: FactoryContact[]
): Array<{
  displayOrder?: number;
  division?: string;
  department?: string;
  personName?: string;
  phoneNumber?: string;
  emailAddress?: string;
  fax?: string;
}> => {
  const validContacts = contacts.filter(
    (contact) =>
      contact.contactPerson?.trim() ||
      contact.department?.trim() ||
      contact.phone?.trim() ||
      contact.fax?.trim() ||
      (contact.email && isValidEmail(contact.email))
  );

  // If no valid contacts, send empty array
  if (validContacts.length === 0) {
    return [];
  }

  return validContacts.map((contact, index) => {
    const officer: {
      displayOrder?: number;
      division?: string;
      department?: string;
      personName?: string;
      phoneNumber?: string;
      emailAddress?: string;
      fax?: string;
    } = {
      displayOrder: Number(index + 1),
    };

    // Map roleType to division (number)
    const division = mapRoleTypeToDivision(contact.roleType);
    if (division) {
      officer.division = division;
    }

    // Only include fields that have actual values
    if (contact.contactPerson?.trim()) {
      officer.personName = contact.contactPerson.trim();
    }
    if (contact.department?.trim()) {
      officer.department = contact.department.trim();
    }
    if (contact.phone?.trim()) {
      officer.phoneNumber = contact.phone.trim();
    }
    if (contact.email && isValidEmail(contact.email.trim())) {
      officer.emailAddress = contact.email.trim();
    }
    if (contact.fax?.trim()) {
      officer.fax = contact.fax.trim();
    }
    return officer;
  });
};

/**
 * Helper function to create partner associations
 */
export const createPartnerAssociations = (
  cooperativeUnions: CooperativeUnion[]
): Array<{ displayOrder: number; name: string }> => {
  const validUnions = cooperativeUnions.filter((union) => union.name?.trim());

  // If no valid unions, send empty array
  if (validUnions.length === 0) {
    return [];
  }

  return validUnions.map((union, index) => ({
    displayOrder: Number(index + 1),
    name: String(union.name.trim()),
  }));
};

/**
 * Helper function to create materials
 */
export const createMaterials = (
  cement: RawMaterial[]
): Array<{
  displayOrder: number;
  cementTypeCode: number;
  cementTypeOther?: string;
  maker: string;
}> => {
  const validMaterials = cement.filter((material) => {
    // Check if manufacturerOrOrigin exists
    if (!material.manufacturerOrOrigin?.trim()) {
      return false;
    }

    // For "other" type, also check if otherType is provided
    if (material.type === 'other' && !material.otherType?.trim()) {
      return false;
    }

    return true;
  });

  // If no valid materials, send empty array
  if (validMaterials.length === 0) {
    return [];
  }

  return validMaterials.map((material, index) => {
    const mappedMaterial: {
      displayOrder: number;
      cementTypeCode: number;
      cementTypeOther?: string;
      maker: string;
    } = {
      displayOrder: Number(index + 1),
      maker: String(material.manufacturerOrOrigin.trim()),
      cementTypeCode: 1, // Default
    };

    // Handle cement type - if "other", use otherType field, otherwise use type code
    if (material.type === 'other' && material.otherType?.trim()) {
      // For "other" type, send the custom type name instead of code
      mappedMaterial.cementTypeCode = 0; // Use 0 to indicate custom type
      mappedMaterial.cementTypeOther = String(material.otherType.trim());
    } else {
      // For standard types (1-6), send the type code
      const typeCode = Number.parseInt(material.type);
      if (!Number.isNaN(typeCode) && typeCode >= 1 && typeCode <= 6) {
        mappedMaterial.cementTypeCode = typeCode;
      } else {
        // Fallback for invalid type codes
        mappedMaterial.cementTypeCode = 1; // Default to standard cement
      }
    }

    return mappedMaterial;
  });
};

/**
 * Helper function to clean API request data
 * Removes empty strings, null values, and empty arrays that might cause backend validation errors
 */

// Check if an array item should be kept
const shouldKeepArrayItem = (item: unknown): boolean => {
  if (typeof item === 'object' && item !== null) {
    return Object.keys(item).length > 0;
  }
  return item != null && item !== '';
};

// Process string values with email validation
const processStringValue = (key: string, value: string): string | null => {
  const isEmailField = key === 'email' || key === 'emailAddress' || key.includes('email');

  if (isEmailField) {
    return isValidEmail(value) ? value.trim() : null;
  }

  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  return trimmed;
};

// Process a single object entry
const processObjectEntry = (
  key: string,
  value: unknown,
  cleaned: Record<string, unknown>
): void => {
  if (value === null) {
    // Allow null values to be sent to backend for proper empty field updates
    cleaned[key] = null;
    return;
  }

  if (value === undefined) {
    return;
  }

  if (typeof value === 'string') {
    const processed = processStringValue(key, value);
    if (processed !== null) {
      cleaned[key] = processed;
    }
    return;
  }

  if (Array.isArray(value)) {
    const cleanedArray = cleanApiRequest(value);
    if (Array.isArray(cleanedArray) && cleanedArray.length > 0) {
      cleaned[key] = cleanedArray;
    }
    return;
  }

  if (typeof value === 'object') {
    const cleanedObject = cleanApiRequest(value);
    if (
      typeof cleanedObject === 'object' &&
      cleanedObject !== null &&
      Object.keys(cleanedObject).length > 0
    ) {
      cleaned[key] = cleanedObject;
    }
    return;
  }

  // Include numbers, booleans, etc.
  cleaned[key] = value;
};

const cleanApiRequest = (data: unknown): unknown => {
  if (Array.isArray(data)) {
    return data.map(cleanApiRequest).filter(shouldKeepArrayItem);
  }

  if (typeof data === 'object' && data !== null) {
    const cleaned: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(data)) {
      processObjectEntry(key, value, cleaned);
    }

    return cleaned;
  }

  return data;
};

/**
 * Maps supplier registration form data to API request format (for creating new suppliers)
 * Converts form field names to API field names and IDs to codes
 */
export const mapSupplierFormRequest = (
  formData: SupplierRegistrationForm,
  departments: DepartmentResponse[] = []
): CreateSupplierRequest => {
  // Map representatives with ID to code conversion
  const departmentContacts = mapRepresentativesToDepartmentContactLists(
    formData.representatives,
    departments
  );

  return {
    partnerClassification: 2, // Supplier
    galiAccountCode: toNumber(formData.galiSupplierCode),
    formalName: formData.officialName || '',
    abbreviationName: formData.abbreviatedName || '',
    branchDepartment: formData.branchDepartment || '',
    partnerNameKana: formData.supplierNameKatakana || '',
    supplierType: getSupplierTypeNumber(formData.supplierType),
    customerCategory: getSupplierCategoryNumber(formData.supplierCategory),

    // Location address
    locationZipCodeFirst: toNumber(formData.currentAddress.postalCode1, 0) || 0,
    locationZipCodeLast: toNumber(formData.currentAddress.postalCode2, 0) || 0,
    locationAddress1: formData.currentAddress.address1 || '',
    locationAddress2: formData.currentAddress.address2 || '',
    locationAddress3: formData.currentAddress.address3 || '',
    locationPhone: formData.currentAddress.phone || '',
    locationFax: formData.currentAddress.fax || '',

    // Headquarters address
    headquartersAddressZipCodeFirst: toNumber(formData.headOfficeAddress.postalCode1, 0) || 0,
    headquartersAddressZipCodeLast: toNumber(formData.headOfficeAddress.postalCode2, 0) || 0,
    headquartersAddress1: formData.headOfficeAddress.address1 || '',
    headquartersAddress2: formData.headOfficeAddress.address2 || '',
    headquartersAddress3: formData.headOfficeAddress.address3 || '',

    // Factory information
    factoryName: formData.factoryAddress?.factoryName || '',
    factoryLocationZipCodeFirst: toNumber(formData.factoryAddress?.postalCode1),
    factoryLocationZipCodeLast: toNumber(formData.factoryAddress?.postalCode2),
    factoryLocation: formData.factoryAddress?.addressLocation || undefined,

    // Certifications
    factoryJisCertNo: formData.certificationInfo?.jisNumber || undefined,
    factoryCertClassification: formData.certificationInfo?.certificationType || undefined,
    factoryHighStrengthCertNo: formData.certificationInfo?.highStrengthNumber || undefined,

    // Qualified personnel - convert to numbers
    qualifiedEngineerConstructionDepartment: toNumber(
      formData.qualifiedPersonnel?.professionalEngineers
    ),
    qualifiedConcreteDiagnostician: toNumber(formData.qualifiedPersonnel?.concreteDiagnosticians),
    qualifiedConcreteChiefEngineer: toNumber(
      formData.qualifiedPersonnel?.seniorConcreteTechnicians
    ),
    qualifiedConcreteEngineer: toNumber(formData.qualifiedPersonnel?.concreteTechnicians),

    // Mixer information
    mixerNominalCapacity: formData.mixerInfo?.ratedCapacity || '',
    mixerFormat: formData.mixerInfo?.type || '',
    mixerCapacity: formData.mixerInfo?.mixerCapacity || '',
    mixerCardinality: toNumber(formData.mixerInfo?.mixerCardinality),
    mixerProductionCompany: formData.mixerInfo?.manufacturer || '',
    mixerWeighingMethod: formData.mixerInfo?.measurementMethod || '',

    // Transport vehicles - convert to numbers
    carrierLarge10t: toNumber(formData.transportVehicles?.large10t),
    carrierMediumSize8t: toNumber(formData.transportVehicles?.medium8t),
    carrierMediumSize7t: toNumber(formData.transportVehicles?.size7t),
    carrierMediumSize5t: toNumber(formData.transportVehicles?.size5t),
    carrierSmall4t: toNumber(formData.transportVehicles?.small4t),
    carrierM3Type3t: toNumber(formData.transportVehicles?.size3t),
    carrierM3Type2t: toNumber(formData.transportVehicles?.size2t),
    other: formData.transportVehicles?.other || undefined,

    // Contact officers - filter valid contacts, send empty array if none
    contactOfficerLists: createContactOfficerLists(formData.factoryContacts || []),

    // Aggregates - filter valid aggregates, send empty array if none
    aggregateCoarseLists: createAggregateLists(formData.rawMaterials?.coarseAggregate || []),

    aggregateFineLists: createAggregateLists(formData.rawMaterials?.fineAggregate || []),

    // Materials - filter valid materials, send empty array if none
    materials: createMaterials(formData.rawMaterials?.cement || []),

    // Partner associations - filter valid unions, send empty array if none
    partnerAssociations: createPartnerAssociations(
      formData.unionMembership?.cooperativeUnions || []
    ),

    // Industry association
    industryAssociation: formData.unionMembership?.industrialUnion || '',

    // Department contacts
    departmentContactLists: departmentContacts,

    // Common cement flag
    isCommonCement: formData.isCommonCement ?? false,
  };
};

/**
 * Validates required fields in supplier registration form
 */
export const validateRequiredFields = (formData: SupplierRegistrationForm): string[] => {
  const errors: string[] = [];

  // Basic required fields
  if (!formData.officialName?.trim()) {
    errors.push('正式名称は必須です');
  }
  if (!formData.abbreviatedName?.trim()) {
    errors.push('略称は必須です');
  }
  if (!formData.supplierNameKatakana?.trim()) {
    errors.push('仕入先名カナは必須です');
  }

  // Address validation
  if (!formData.currentAddress.address1?.trim()) {
    errors.push('現住所は必須です');
  }
  if (
    !formData.currentAddress.postalCode1?.trim() ||
    !formData.currentAddress.postalCode2?.trim()
  ) {
    errors.push('郵便番号は必須です');
  }

  // At least one representative is required
  const validRepresentatives = formData.representatives.filter(
    (rep) => rep.department && rep.salesPerson
  );
  if (validRepresentatives.length === 0) {
    errors.push('野原担当者を最低1名選択してください');
  }

  return errors;
};
